import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Sparkles, Share2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo92TransferenciaSistema() {
    const [situacion, setSituacion] = useState('');
    const [patron, setPatron] = useState('');
    const [intervencion, setIntervencion] = useState('');
    const [accion, setAccion] = useState('');
    const [metrica, setMetrica] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [nivelTransferencia, setNivelTransferencia] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/transferencia_sistema`, { withCredentials: true });
                if (data.data) {
                    if (data.data.situacion) setSituacion(data.data.situacion);
                    if (data.data.patron) setPatron(data.data.patron);
                    if (data.data.intervencion) setIntervencion(data.data.intervencion);
                    if (data.data.accion) setAccion(data.data.accion);
                    if (data.data.metrica) setMetrica(data.data.metrica);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'transferencia_sistema', data: { situacion, patron, intervencion, accion, metrica } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [situacion, patron, intervencion, accion, metrica]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [situacion, patron, intervencion, accion, metrica, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (situacion.trim().length > 0 && patron.trim().length > 0 && intervencion.trim().length > 0 && accion.trim().length > 0 && metrica.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/transferencia-sistema/submit`, {
                situacion, patron, intervencion, accion, metrica
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setNivelTransferencia(data.nivel_transferencia);
            setSubmitted(true);
            toast.success('Transferencia del sistema completada');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#7A9BA8] to-[#5F8494] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Share2 className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="transferencia-sistema-success-title">Sistema transferido</h2>
                    <p className="text-[#7A9BA8] font-medium mb-4">Has aplicado tu sistema a un nuevo contexto</p>
                    {nivelTransferencia && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Transferencia: {nivelTransferencia}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#7A9BA8] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#7A9BA8] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="transferencia_sistema" chapter={9} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-transferencia-sistema">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/usuario" className="flex items-center gap-2 text-[#3E5F6E] hover:text-[#2F4A57]"><ArrowLeft className="h-5 w-5" /><span className="text-sm font-medium">Volver</span></Link>
                    <span className="text-xs text-[#9AA5B1]">{savingDraft ? 'Guardando...' : 'Borrador guardado'}</span>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2"><div className="w-10 h-10 bg-[#3E5F6E] rounded-xl flex items-center justify-center"><Share2 className="h-5 w-5 text-white" /></div><span className="text-xs font-semibold text-[#9AA5B1] uppercase tracking-wider">Capítulo 9 · Módulo 9.2</span></div>
                    <h1 className="text-2xl sm:text-3xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Transferencia del sistema</h1>
                    <p className="text-[#616E7C]">Aplica tu sistema SENDA a un contexto nuevo. Demuestra que puedes generalizar lo aprendido.</p>
                </div>
                <div className="space-y-6">
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Situación nueva</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">Describe una situación nueva donde quieres aplicar tu sistema.</p>
                        <Textarea value={situacion} onChange={e => setSituacion(e.target.value)} placeholder="La nueva situación es..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="transferencia-situacion" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Patrón identificado</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Qué patrón conductual se activa en esta situación?</p>
                        <Textarea value={patron} onChange={e => setPatron(e.target.value)} placeholder="El patrón que se activa es..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="transferencia-patron" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Intervención planificada</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Qué intervención de tu sistema aplicarás?</p>
                        <Textarea value={intervencion} onChange={e => setIntervencion(e.target.value)} placeholder="Mi intervención será..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="transferencia-intervencion" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Acción concreta</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cuál es la acción específica que ejecutarás?</p>
                        <Textarea value={accion} onChange={e => setAccion(e.target.value)} placeholder="La acción que haré es..." className="min-h-[80px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="transferencia-accion" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-[#1F2A30] mb-2 block">Métrica de éxito</Label>
                        <p className="text-xs text-[#9AA5B1] mb-2">¿Cómo sabrás que la transferencia fue exitosa?</p>
                        <Textarea value={metrica} onChange={e => setMetrica(e.target.value)} placeholder="Sabré que funciona cuando..." className="min-h-[60px] border-[#DCE3E8] focus:border-[#3E5F6E]" data-testid="transferencia-metrica" />
                    </div>
                    <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="w-full bg-[#3E5F6E] hover:bg-[#2F4A57] text-white py-3 rounded-xl" data-testid="submit-transferencia-sistema">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analizando...</> : 'Completar transferencia'}
                    </Button>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
